import { Card, CardContent, Input, Label } from "@learn-me/ui";
import { useCourseWizard } from "@/hooks";
import { BookOpen, Clock, GraduationCap, Lightbulb } from "lucide-react";

export function WizardStep1Config() {
  const {
    topic,
    setTopic,
    level,
    setLevel,
    duration,
    setDuration,
    additionalNotes,
    setAdditionalNotes,
    levelLabels,
    durationLabels,
    estimatedChapters,
  } = useCourseWizard();

  return (
    <div className="space-y-6">
      {/* Topic input */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-primary-100 rounded-lg">
                <Lightbulb className="h-5 w-5 text-primary-600" />
              </div>
              <div className="flex-1">
                <Label htmlFor="topic" className="text-base font-medium">
                  ¿Sobre que tema quieres crear el curso?
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Se especifico para obtener mejores resultados. Por ejemplo:
                  "Introduccion a Python para principiantes" o "Marketing
                  digital para pequenas empresas".
                </p>
              </div>
            </div>
            <Input
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Ej: Fundamentos de inteligencia artificial para negocios"
              className="text-lg py-6"
              autoFocus
            />
            {topic.length > 0 && topic.length < 3 && (
              <p className="text-sm text-amber-600">
                El tema debe tener al menos 3 caracteres
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Level selection */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <GraduationCap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <Label className="text-base font-medium">Nivel del curso</Label>
                <p className="text-sm text-gray-500 mt-1">
                  Selecciona el nivel de conocimiento previo requerido
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {(["BEGINNER", "INTERMEDIATE", "ADVANCED"] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLevel(l)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    level === l
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <span
                    className={`font-medium ${level === l ? "text-primary-700" : "text-gray-900"}`}
                  >
                    {levelLabels[l]}
                  </span>
                  <p className="text-sm text-gray-500 mt-1">
                    {l === "BEGINNER" && "Sin conocimientos previos requeridos"}
                    {l === "INTERMEDIATE" &&
                      "Requiere conocimientos basicos del tema"}
                    {l === "ADVANCED" && "Para expertos que buscan profundizar"}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Duration selection */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <Label className="text-base font-medium">
                  Duracion estimada
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Esto determina la cantidad de capitulos y profundidad del
                  contenido
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {(["SHORT", "MEDIUM", "LONG"] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDuration(d)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    duration === d
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <span
                    className={`font-medium ${duration === d ? "text-primary-700" : "text-gray-900"}`}
                  >
                    {durationLabels[d]}
                  </span>
                  <p className="text-sm text-gray-500 mt-1">
                    {d === "SHORT" && "3-5 capitulos, contenido conciso"}
                    {d === "MEDIUM" && "5-8 capitulos, balance ideal"}
                    {d === "LONG" && "8-12 capitulos, muy detallado"}
                  </p>
                </button>
              ))}
            </div>
            {duration && (
              <p className="text-sm text-gray-600 text-center">
                Se generaran aproximadamente{" "}
                <span className="font-medium">
                  {estimatedChapters.min}-{estimatedChapters.max} capitulos
                </span>
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Additional notes */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BookOpen className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <Label htmlFor="notes" className="text-base font-medium">
                  Notas adicionales{" "}
                  <span className="text-gray-400 font-normal">(opcional)</span>
                </Label>
                <p className="text-sm text-gray-500 mt-1">
                  Agrega cualquier instruccion especial, enfoque particular o
                  temas especificos que quieras incluir
                </p>
              </div>
            </div>
            <textarea
              id="notes"
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder="Ej: Enfocarse en casos practicos del sector retail. Incluir ejemplos con datos reales. Evitar terminologia muy tecnica."
              className="w-full min-h-[100px] p-3 border rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              rows={3}
              maxLength={2000}
            />
            <p className="text-xs text-gray-500 mt-1 text-right">
              {additionalNotes.length}/2000 caracteres
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
